import { lazy } from 'react';
import { Routes as configRoutes } from '../config/Routes';
import {
  cashInDetailsAbility,
  cashOutDetailsAbility,
  clientsAbility,
  clientsDetailsAbility,
  homeAbility,
  paymentMethodsAbility,
  transactionsAbility,
  settlementsHistoryAbility,
  accountsAbility,
  accountCreateAbility,
  merchantSelfSettingsAbility,
  merchantsAbility,
  supportAbility,
  notesListAbility,
  refundsAbility,
  chargebacksAbility,
  balanceReportListAbility,
  cashOutRequestManualAbility,
  cashInRequestManualAbility,
  notificationsAbility,
  beneficiariesAbility,
  batchCreationAbility,
  transferAbility,
  accountEditAbility,
  topUpAbility,
  recurringPaymentsAbility,
  authusAbility,
  upcomingPaymentsAbility,
  paymentOrderAbility,
} from '../utils/Capability';

const PaymentOrdersBatchesList = lazy(() => import('../pages/PaymentOrdersBatches/PaymentOrdersBatchesList'));
const PaymentOrdersList = lazy(() => import('../pages/PaymentOrders/PaymentOrdersList'));
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const BalanceReportList = lazy(() => import('../pages/Balance/BalanceReportList'));
const DirectCashInList = lazy(() => import('../pages/Cashins/CashInList'));
const DirectCashOutList = lazy(() => import('../pages/Cashouts/CashOutList'));
const MerchantsList = lazy(() => import('../pages/Merchants/MerchantsList'));
const SignUpRequestsList = lazy(() => import('../pages/Merchants/SignUpRequestsList'));
const DirectPaymentMethodsList = lazy(() => import('../pages/PaymentMethods/PaymentMethodsList'));
const RefundDetails = lazy(() => import('../pages/Refund/RefundDetails'));
const RefundList = lazy(() => import('../pages/Refund/RefundList'));
const ChargebacksList = lazy(() => import('../pages/Chargeback/ChargebacksList'));
const TransactionDetails = lazy(() => import('../pages/Transactions/TransactionDetails'));
const ClientsList = lazy(() => import('../pages/Clients/ClientsList'));
const ClientDetails = lazy(() => import('../pages/Clients/ClientDetails'));
const SettlementDetails = lazy(() => import('../pages/Settlements/SettlementDetails'));
const AccountsList = lazy(() => import('../pages/Accounts/AccountsList'));
const DirectSettlementsHistory = lazy(() => import('../pages/Settlements/SettlementsList'));
const Settings = lazy(() => import('../pages/Settings/Settings'));
const MerchantAccount = lazy(() => import('../pages/Accounts/MerchantAccount'));
const InternalAccount = lazy(() => import('../pages/Accounts/InternalAccount'));
const Notifications = lazy(() => import('../pages/Notifications/Notifications'));
const NotesList = lazy(() => import('../pages/Notes/NotesList'));
const NoteDetails = lazy(() => import('../pages/Notes/NoteDetails'));
const Support = lazy(() => import('../pages/Support/Support'));
const PresetsList = lazy(() => import('../pages/Accounts/PresetsList'));
const CashOutRequest = lazy(() => import('../pages/Cashouts/CashOutRequest'));
const CashInRequest = lazy(() => import('../pages/Cashins/CashInRequest'));
const CreateNote = lazy(() => import('../pages/Notes/CreateNote'));
const BeneficiariesList = lazy(() => import('../pages/Beneficiaries/BeneficiariesList'));
const BeneficiaryDetails = lazy(() => import('../pages/Beneficiaries/BeneficiaryDetails'));
const RecurringPaymentsList = lazy(() => import('../pages/RecurringPayments/RecurringPaymentsList'));
const BatchCashouts = lazy(() => import('../pages/BatchCreation/BatchCashouts'));
const BatchCashoutsUpload = lazy(() => import('../pages/BatchCreation/BatchCashoutsUpload'));
const TransferList = lazy(() => import('../pages/Transfers/TransferList'));
const TransferRequest = lazy(() => import('../pages/Transfers/TransferRequest'));
const InternalTransfer = lazy(() => import('../pages/InternalTransfer/InternalTransfer'));
const TopUpsList = lazy(() => import('../pages/TopUps/TopUpsList'));
const TopUpDetails = lazy(() => import('../pages/TopUps/TopUpDetails'));
const FAQS = lazy(() => import('../pages/FAQS/FAQS'));
const FAQDeposit = lazy(() => import('../pages/FAQS/FAQDeposit'));
const FAQWithdrawals = lazy(() => import('../pages/FAQS/FAQWithdrawals'));
const FAQRefunds = lazy(() => import('../pages/FAQS/FAQRefunds'));
const FAQChargebacks = lazy(() => import('../pages/FAQS/FAQChargebacks'));
const FAQSettlements = lazy(() => import('../pages/FAQS/FAQSettlements'));
const FAQPaymentmethods = lazy(() => import('../pages/FAQS/FAQPaymentMethods'));
const FAQSettings = lazy(() => import('../pages/FAQS/FAQSettings'));
const FAQSupport = lazy(() => import('../pages/FAQS/FAQSupport'));
const OnekeyVerify = lazy(() => import('../pages/OneKeyVerify/OnekeyVerify'));
const ReferredMerchants = lazy(() => import('../pages/ReferredMerchants/ReferredMerchantList'));
const UpcomingPayments = lazy(() => import('../pages/UpcomingPayments/UpcomingPayments'));

const KYCTool = lazy(() => import('../pages/KYCTool/KYCTool'));
const RollingReserveList = lazy(() => import('../pages/RollingReserve/RollingReserveList'));
const WalletTranfer = lazy(() => import('../pages/WalletTransfer/WalletTranfer'));
const BalanceReportListWallet = lazy(() => import('../pages/Balance/BalanceReportListWallet'));
const SubMerchantBalanceList = lazy(() => import('../pages/Balance/SubMerchantBalanceList'));
const SubMerchantBalanceDetail = lazy(() => import('../pages/Balance/SubMerchantBalanceDetail'));
const PspMerchantsList = lazy(() => import('../pages/PspMerchants/PspMerchantsList'));

const privateRoutes = [
  {
    Component: Dashboard,
    path: configRoutes.DASHBOARD,
    capabilities: homeAbility,
  },
  {
    Component: DirectCashInList,
    path: configRoutes.CASH_IN,
    breadcrumbs: [{ labelId: 'labelCashIn' }],
    capabilities: transactionsAbility.cashInList,
  },
  {
    Component: PaymentOrdersBatchesList,
    path: configRoutes.PAYMENT_ORDERS_BATCHES,
    breadcrumbs: [{ labelId: 'labelPaymentOrdersBatches' }],
    capabilities: paymentOrderAbility,
  },
  {
    Component: PaymentOrdersList,
    path: configRoutes.PAYMENT_ORDERS_BATCHES_DETAILS,
    breadcrumbs: [
      { to: configRoutes.PAYMENT_ORDERS_BATCHES, labelId: 'labelPaymentOrdersBatches' },
      { labelId: 'labelPaymentOrders' }],
    capabilities: cashInDetailsAbility,
  },
  {
    Component: PaymentOrdersList,
    path: configRoutes.PAYMENT_ORDERS,
    breadcrumbs: [{ labelId: 'labelPaymentOrders' }],
    capabilities: paymentOrderAbility,
  },
  {
    Component: DirectCashOutList,
    path: configRoutes.CASH_OUT,
    breadcrumbs: [{ labelId: 'labelCashOut' }],
    capabilities: transactionsAbility.cashOutList,
  },
  {
    Component: UpcomingPayments,
    path: configRoutes.UPCOMING_PAYMENTS,
    breadcrumbs: [{ labelId: 'labelUpcomingPayments' }],
    capabilities: upcomingPaymentsAbility,
  },
  {
    Component: NotesList,
    path: configRoutes.NOTES,
    breadcrumbs: [{ labelId: 'labelCreditAndDebitNotes' }],
    capabilities: notesListAbility,
  },
  {
    Component: NoteDetails,
    path: configRoutes.NOTE_DETAILS,
    breadcrumbs: [{ to: configRoutes.NOTES, labelId: 'labelNotes' }, { labelId: 'labelNoteDetails' }],
    capabilities: notesListAbility,
  },
  {
    Component: CreateNote,
    path: configRoutes.NOTE_CREATE,
    breadcrumbs: [{ to: configRoutes.NOTES, labelId: 'labelCreditAndDebitNotes' }, { labelId: 'labelNewNote' }],
    capabilities: notesListAbility.notesCreate,
  },
  {
    Component: RefundList,
    path: configRoutes.REFUNDS,
    breadcrumbs: [{ labelId: 'labelRefunds' }],
    capabilities: refundsAbility,
  },
  {
    Component: ChargebacksList,
    path: configRoutes.CHARGEBACKS,
    breadcrumbs: [{ labelId: 'labelChargebacks' }],
    capabilities: chargebacksAbility,
  },
  {
    Component: TransactionDetails,
    path: configRoutes.CHARGEBACKS_DETAILS_ALL_IN,
    breadcrumbs: [{ to: configRoutes.CHARGEBACKS, labelId: 'labelChargebacks' }, { to: configRoutes.CASH_IN, labelId: 'labelCashIn' }, { labelId: 'labelTransactionDetails' }],
    capabilities: cashInDetailsAbility,
  },
  {
    Component: RefundDetails,
    path: configRoutes.REFUNDS_DETAILS,
    breadcrumbs: [{ to: configRoutes.REFUNDS, labelId: 'labelRefunds' }, { labelId: 'labelRefundDetails' }],
    capabilities: refundsAbility.refundViewDetails,
  },
  {
    Component: BalanceReportList,
    path: configRoutes.BALANCE_REPORT,
    breadcrumbs: [{ labelId: 'labelBalanceReport' }],
    capabilities: balanceReportListAbility,
  },
  {
    Component: SubMerchantBalanceList,
    path: configRoutes.SUBMERCHANT_BALANCE,
    breadcrumbs: [{ labelId: 'labelBalance' }],
    capabilities: balanceReportListAbility,
  },
  {
    Component: SubMerchantBalanceList,
    path: configRoutes.SUBMERCHANT_BALANCE_INTERNAL,
    breadcrumbs: [{ labelId: 'labelBalance' }],
    capabilities: balanceReportListAbility,
  },
  {
    Component: SubMerchantBalanceDetail,
    path: configRoutes.SUBMERCHANT_BALANCE_DETAIL,
    breadcrumbs: [{ labelId: 'labelSubMerchantBalanceDetail' }],
    capabilities: balanceReportListAbility,
  },
  {
    Component: BalanceReportListWallet,
    path: configRoutes.BALANCE_REPORT_WALLET,
    breadcrumbs: [{ labelId: 'labelBalanceReport' }],
    capabilities: balanceReportListAbility,
  },
  {
    Component: WalletTranfer,
    path: configRoutes.WALLET_TRANFER,
    breadcrumbs: [{ labelId: 'labelWalletTranfer' }],
    capabilities: balanceReportListAbility,
  },
  {
    Component: DirectPaymentMethodsList,
    path: configRoutes.PAYMENT_METHODS,
    breadcrumbs: [{ labelId: 'labelPaymentMethods' }],
    capabilities: paymentMethodsAbility,
  },
  {
    Component: CashOutRequest,
    path: configRoutes.CASH_OUT_CREATE,
    breadcrumbs: [{ to: configRoutes.CASH_OUT, labelId: 'labelCashOut' }, { labelId: 'labelRequestCashout' }],
    capabilities: cashOutRequestManualAbility,
  },
  {
    Component: CashInRequest,
    path: configRoutes.CASH_IN_CREATE,
    breadcrumbs: [{ to: configRoutes.CASH_IN, labelId: 'labelCashIn' }, { labelId: 'labelRequestCashin' }],
    capabilities: cashInRequestManualAbility,
  },
  {
    Component: CashInRequest,
    path: configRoutes.RECURRING_PAYMENTS_IN_CREATE,
    breadcrumbs: [{ to: configRoutes.RECURRING_PAYMENTS, labelId: 'navRecurringPayments' }, { labelId: 'navRecurringPaymentsRequest' }],
    capabilities: cashInRequestManualAbility,
  },
  {
    Component: TransactionDetails,
    path: configRoutes.TRANSACTION_DETAILS_IN,
    breadcrumbs: [{ to: configRoutes.CASH_IN, labelId: 'labelCashIn' }, { labelId: 'labelTransactionDetails' }],
    capabilities: cashInDetailsAbility,
  },
  {
    Component: TransactionDetails,
    path: configRoutes.TRANSACTION_DETAILS_OUT,
    breadcrumbs: [{ to: configRoutes.CASH_OUT, labelId: 'labelCashOut' }, { labelId: 'labelTransactionDetails' }],
    capabilities: cashOutDetailsAbility,
  },
  {
    Component: ClientsList,
    path: configRoutes.CLIENTS,
    breadcrumbs: [{ labelId: 'labelClients' }],
    capabilities: clientsAbility,
  },
  {
    Component: ClientDetails,
    path: configRoutes.CLIENT_DETAILS,
    breadcrumbs: [{ to: configRoutes.CLIENTS, labelId: 'labelClients' }, { labelId: 'labelClientDetails' }],
    capabilities: clientsDetailsAbility,
  },
  {
    Component: BeneficiariesList,
    path: configRoutes.BENEFICIARIES,
    breadcrumbs: [{ labelId: 'navBeneficiaries' }],
    capabilities: beneficiariesAbility,
  },
  {
    Component: BeneficiaryDetails,
    path: configRoutes.BENEFICIARY_DETAILS,
    breadcrumbs: [{ to: configRoutes.BENEFICIARIES, labelId: 'navBeneficiaries' }, { labelId: 'labelBeneficiaryDetails' }],
    capabilities: beneficiariesAbility.beneficiaryViewDetails,
  },
  {
    Component: RecurringPaymentsList,
    path: configRoutes.RECURRING_PAYMENTS,
    breadcrumbs: [{ labelId: 'navRecurringPayments' }],
    capabilities: recurringPaymentsAbility,
  },
  {
    Component: DirectSettlementsHistory,
    path: configRoutes.SETTLEMENTS,
    breadcrumbs: [{ labelId: 'labelSettlements' }],
    capabilities: settlementsHistoryAbility,
  },
  {
    Component: SettlementDetails,
    path: configRoutes.SETTLEMENT_DETAILS,
    breadcrumbs: [{ to: configRoutes.SETTLEMENTS, labelId: 'labelSettlements' }, { labelId: 'labelSettlementDetails' }],
    capabilities: homeAbility,
  },
  {
    Component: AccountsList,
    path: configRoutes.ACCOUNTS,
    breadcrumbs: [{ labelId: 'labelAccounts' }],
    capabilities: accountsAbility,
  },
  {
    Component: PresetsList,
    path: configRoutes.ACCOUNT_PRESETS,
    breadcrumbs: [{ labelId: 'labelPresets' }],
    capabilities: accountCreateAbility,
  },
  {
    Component: Settings,
    path: configRoutes.SETTINGS,
    breadcrumbs: [{ labelId: 'labelSettings' }],
    capabilities: merchantSelfSettingsAbility,
  },
  {
    Component: MerchantsList,
    path: configRoutes.MERCHANTS,
    breadcrumbs: [{ labelId: 'labelMerchants' }],
    capabilities: merchantsAbility.merchantsList,
  },
  {
    Component: PspMerchantsList,
    path: configRoutes.PSP_MERCHANTS,
    breadcrumbs: [{ labelId: 'labelPspMerchants' }],
    capabilities: merchantsAbility.merchantsList,
  },
  {
    Component: SignUpRequestsList,
    path: configRoutes.SIGN_UP_REQUESTS,
    breadcrumbs: [{ labelId: 'labelSignUpRequests' }],
    capabilities: merchantsAbility.signUpRequestsList,
  },
  {
    Component: Settings,
    path: configRoutes.MERCHANT_SETTINGS,
    breadcrumbs: [{ to: configRoutes.MERCHANTS, labelId: 'labelMerchants' }, { labelId: 'labelMerchantSettings' }],
    capabilities: merchantsAbility.merchantSettings,
  },
  {
    Component: Support,
    path: configRoutes.SUPPORT,
    breadcrumbs: [{ labelId: 'labelSupport', showFaq: true, faqUrl: configRoutes.FAQ_SUPPORT }],
    capabilities: supportAbility,
  },
  {
    Component: Support,
    path: configRoutes.SUPPORT_CLOSE_TICKET,
    breadcrumbs: [{ labelId: 'labelSupport' }],
    capabilities: supportAbility,
  },
  {
    Component: MerchantAccount,
    path: configRoutes.ACCOUNT_CREATE_MERCHANT,
    breadcrumbs: [{ to: configRoutes.ACCOUNTS, labelId: 'labelAccounts' }, { labelId: 'labelNewAccount' }],
    capabilities: accountCreateAbility,
  },
  {
    Component: InternalAccount,
    path: configRoutes.ACCOUNT_CREATE_INTERNAL,
    breadcrumbs: [{ to: configRoutes.ACCOUNTS, labelId: 'labelAccounts' }, { labelId: 'labelNewInternalAccount' }],
    capabilities: accountCreateAbility,
  },
  {
    Component: MerchantAccount,
    path: configRoutes.ACCOUNT_EDIT_MERCHANT,
    breadcrumbs: [{ to: configRoutes.ACCOUNTS, labelId: 'labelAccounts' }, { labelId: 'labelEditAccount' }],
    capabilities: accountEditAbility,
  },
  {
    Component: InternalAccount,
    path: configRoutes.ACCOUNT_EDIT_INTERNAL,
    breadcrumbs: [{ to: configRoutes.ACCOUNTS, labelId: 'labelAccounts' }, { labelId: 'labelEditInternalAccount' }],
    capabilities: accountEditAbility,
  },
  {
    Component: Notifications,
    path: configRoutes.NOTIFICATIONS,
    breadcrumbs: [{ labelId: 'navNotifications' }],
    capabilities: notificationsAbility,
  },
  {
    Component: BatchCashouts,
    path: configRoutes.BATCH_CASHOUTS,
    breadcrumbs: [{ labelId: 'navCashOutBatch' }],
    capabilities: transactionsAbility.cashOutList,
  },
  {
    Component: BatchCashoutsUpload,
    path: configRoutes.BATCH_CASHOUTS_UPLOAD,
    breadcrumbs: [{ to: configRoutes.BATCH_CASHOUTS, labelId: 'navCashOutBatch' }, { labelId: 'labelCashOutBatchUpload' }],
    capabilities: batchCreationAbility.cashOutRequestBatch,
  },
  {
    Component: TransferList,
    path: configRoutes.TRANSFER,
    breadcrumbs: [{ labelId: 'navTransfer' }],
    capabilities: transferAbility,
  },
  {
    Component: TransferRequest,
    path: configRoutes.TRANSFER_CREATE,
    breadcrumbs: [{ to: configRoutes.TRANSFER, labelId: 'navTransfer' }, { labelId: 'labelRequestTransfer' }],
    capabilities: transferAbility.transferCreate,
  },
  {
    Component: InternalTransfer,
    path: configRoutes.COMMERCIAL_RELATIONSHIP,
    breadcrumbs: [{ labelId: 'navInternalTransfers' }],
    capabilities: transferAbility,
  },
  {
    Component: TopUpsList,
    path: configRoutes.TOP_UPS,
    breadcrumbs: [{ labelId: 'labelTopUps' }],
    capabilities: topUpAbility,
  },
  {
    Component: TopUpDetails,
    path: configRoutes.TOP_UP_DETAILS,
    breadcrumbs: [{ to: configRoutes.TOP_UPS, labelId: 'labelTopUps' }, { labelId: 'labelTopUpDetails' }],
    capabilities: topUpAbility,
  },
  {
    Component: FAQS,
    path: configRoutes.FAQS,
    breadcrumbs: [{ labelId: 'FAQs' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQDeposit,
    path: configRoutes.FAQ_DEPOSIT,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Deposits' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQWithdrawals,
    path: configRoutes.FAQ_WITHDRAWALS,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Withdrawals' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQRefunds,
    path: configRoutes.FAQ_REFUNDS,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Refunds' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQChargebacks,
    path: configRoutes.FAQ_CHARGEBACKS,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Chargebacks' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQSettlements,
    path: configRoutes.FAQ_SETTLEMENTS,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Settlements' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQPaymentmethods,
    path: configRoutes.FAQ_PAYMENT_METHODS,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Payment Methods' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQSettings,
    path: configRoutes.FAQ_SETTINGS,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Settings' }],
    capabilities: homeAbility,
  },
  {
    Component: FAQSupport,
    path: configRoutes.FAQ_SUPPORT,
    breadcrumbs: [{ to: configRoutes.FAQS, labelId: 'FAQS' }, { labelId: 'Support' }],
    capabilities: homeAbility,
  },
  {
    Component: OnekeyVerify,
    path: configRoutes.ONKEYVERIFY,
    breadcrumbs: [{ to: configRoutes.ONKEYVERIFY, labelId: 'Onkey verify' }],
    capabilities: homeAbility,
  },
  {
    Component: ReferredMerchants,
    path: configRoutes.REFERRED_MERCHANTS,
    breadcrumbs: [{ to: configRoutes.REFERRED_MERCHANTS, labelId: 'labelReferred' }],
    capabilities: homeAbility,
  },
  {
    Component: KYCTool,
    path: configRoutes.KYC,
    breadcrumbs: [{ labelId: 'labelKycTool' }],
    capabilities: authusAbility,
  },
  {
    Component: RollingReserveList,
    path: configRoutes.ROLLING_RESERVE,
    breadcrumbs: [{ labelId: 'labelRollingReserve' }],
    capabilities: homeAbility,
  },
];

export default privateRoutes;
